import React, { useEffect, useState } from "react";
import { Cloud, Sun } from "lucide-react";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  CartesianGrid,
  Cell,
  Legend,
} from "recharts";
import { api, fmtInt, fmtDec, fmtMWh } from "../lib/api";

const CAT_COLORS = {
  Laborable: "#14532D",
  Sábado: "#0891B2",
  "Domingo y festivos": "#B45309",
  Nocturno: "#0F1A14",
};

const tooltipStyle = {
  background: "white",
  border: "1px solid #E5E7EB",
  borderRadius: 8,
  fontSize: 12,
  boxShadow: "0 2px 8px rgba(0,0,0,0.06)",
};

/**
 * Componente de "Consumo por tipo de día".
 * - Si recibe `codigoLinea` muestra el desglose de esa línea.
 * - Si recibe `lote` muestra el desglose para ese lote.
 * - Sin filtros: agregado de toda la flota accesible al usuario.
 *
 * Permite togglear el factor climatológico (consumo conservador incluye HVAC ~+15%).
 */
export default function ConsumoTipoDia({ codigoLinea, lote, title = "Consumo por tipo de día", compact = false }) {
  const [aplicarClima, setAplicarClima] = useState(false);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    let alive = true;
    const params = new URLSearchParams();
    if (lote) params.set("lote", lote);
    if (codigoLinea) params.set("codigo_linea", codigoLinea);
    params.set("aplicar_clima", aplicarClima ? "true" : "false");
    setLoading(true);
    setError("");
    api.get(`/consumo-tipo-dia?${params.toString()}`)
      .then((r) => { if (alive) setData(r.data); })
      .catch((e) => { if (alive) setError(e?.response?.data?.detail || "Error cargando datos"); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [lote, codigoLinea, aplicarClima]);

  if (loading) {
    return (
      <div className="crtm-card p-5 text-gray-500 text-sm" data-testid="consumo-tipodia-loading">
        Cargando consumo por tipo de día…
      </div>
    );
  }
  if (error) {
    return (
      <div className="crtm-card p-5 text-amber-700 text-sm" data-testid="consumo-tipodia-error">
        {error}
      </div>
    );
  }
  if (!data) return null;

  const chartData = data.por_categoria.map((c) => ({
    ...c,
    kwh_mwh: +(c.kwh_anual / 1000).toFixed(1),
  }));

  return (
    <div className="crtm-card p-5" data-testid="consumo-tipodia-card">
      <div className="flex items-center justify-between gap-4 mb-3 flex-wrap">
        <div>
          <h3 className="font-heading font-semibold text-gray-900 text-[15px]">{title}</h3>
          <p className="text-xs text-gray-500 mt-0.5">
            Agregado por categoría: Laborable / Sábado / Domingo y festivos / Nocturno.
            {aplicarClima ? " Factor conservador (HVAC) aplicado." : " Factor base (sin penalización climática)."}
          </p>
        </div>
        <button
          type="button"
          onClick={() => setAplicarClima((v) => !v)}
          data-testid="consumo-tipodia-clima-toggle"
          className={
            "px-3 py-1.5 rounded-md text-xs font-semibold border inline-flex items-center gap-1.5 transition " +
            (aplicarClima
              ? "bg-[#0F1A14] text-white border-[#0F1A14]"
              : "bg-white text-gray-700 border-gray-200 hover:bg-gray-50")
          }
        >
          {aplicarClima ? <Cloud size={14} /> : <Sun size={14} />}
          {aplicarClima ? "Con clima (HVAC)" : "Sin clima"}
        </button>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-4">
        {chartData.map((c) => (
          <div
            key={c.categoria}
            className="rounded-lg p-3 border"
            style={{ borderColor: CAT_COLORS[c.categoria] + "33", background: CAT_COLORS[c.categoria] + "0a" }}
            data-testid={`consumo-tipodia-stat-${c.categoria}`}
          >
            <div className="text-[11px] font-semibold uppercase tracking-wide" style={{ color: CAT_COLORS[c.categoria] }}>
              {c.categoria}
            </div>
            <div className="text-lg font-heading font-semibold text-gray-900 mt-0.5">
              {fmtMWh(c.kwh_anual)}
            </div>
            <div className="text-[11px] text-gray-500 mt-0.5">
              {fmtDec(c.porcentaje_kwh, 1)}% · {fmtInt(c.kwh_dia_medio)} kWh/día medio
            </div>
            <div className="text-[11px] text-gray-500">
              {fmtInt(c.veh_km_anual)} veh-km/año
            </div>
          </div>
        ))}
      </div>

      <ResponsiveContainer width="100%" height={compact ? 180 : 220}>
        <BarChart data={chartData} margin={{ top: 5, right: 10, bottom: 5, left: 0 }}>
          <CartesianGrid stroke="#F3F4F6" vertical={false} />
          <XAxis dataKey="categoria" tick={{ fontSize: 11, fill: "#4B5563" }} />
          <YAxis tick={{ fontSize: 11, fill: "#9CA3AF" }} unit=" MWh" />
          <Tooltip
            contentStyle={tooltipStyle}
            formatter={(v, n) => [`${fmtDec(v, 1)} MWh/año`, "Consumo anual"]}
          />
          <Bar dataKey="kwh_mwh" radius={[4, 4, 0, 0]} maxBarSize={60}>
            {chartData.map((c) => (
              <Cell key={c.categoria} fill={CAT_COLORS[c.categoria] || "#374151"} />
            ))}
          </Bar>
        </BarChart>
      </ResponsiveContainer>

      {!compact && data.filas?.length > 0 && (
        <div className="mt-4 overflow-x-auto" data-testid="consumo-tipodia-detalle">
          <table className="w-full text-xs">
            <thead className="text-gray-500 uppercase tracking-wide text-[10px]">
              <tr className="border-b border-gray-100">
                <th className="text-left py-2 font-medium">Categoría</th>
                <th className="text-left py-2 font-medium">Temporada</th>
                <th className="text-right py-2 font-medium">Expediciones/año</th>
                <th className="text-right py-2 font-medium">Veh-km/año</th>
                <th className="text-right py-2 font-medium">MWh/año</th>
                <th className="text-right py-2 font-medium">% kWh</th>
                <th className="text-right py-2 font-medium">kWh/día medio</th>
              </tr>
            </thead>
            <tbody>
              {data.filas.map((f, i) => (
                <tr key={i} className="border-b border-gray-50">
                  <td className="py-1.5">
                    <span
                      className="inline-flex items-center gap-1.5 font-semibold"
                      style={{ color: CAT_COLORS[f.categoria] || "#374151" }}
                    >
                      <span
                        className="inline-block w-2 h-2 rounded-full"
                        style={{ background: CAT_COLORS[f.categoria] || "#374151" }}
                      />
                      {f.categoria}
                    </span>
                  </td>
                  <td className="py-1.5 text-gray-700">{f.temporada}</td>
                  <td className="py-1.5 text-right tabular-nums">{fmtInt(f.n_expediciones)}</td>
                  <td className="py-1.5 text-right tabular-nums">{fmtInt(f.veh_km_anual)}</td>
                  <td className="py-1.5 text-right tabular-nums font-semibold">{fmtDec(f.kwh_anual / 1000, 1)}</td>
                  <td className="py-1.5 text-right tabular-nums">{fmtDec(f.porcentaje_kwh, 1)}%</td>
                  <td className="py-1.5 text-right tabular-nums">{fmtInt(f.kwh_dia_medio)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
